import { connect } from 'react-redux';
import { goBack, push } from 'redux-first-history';

import MoveInCostsPage from './component';
import { DEFAULT_ERROR, MOVE_IN_COSTS_SETTINGS_UPDATED } from '@rental-manager/constants/NotificationConstants';
import { saveMoveInCostsSettings } from '@rental-manager/actions/OnboardingActions';
import { getIdVerificationInfo, getLandlordStatus } from '@rental-manager/actions/requests';
import { closeNotification, showModalNotification } from '@common/actions/NotificationActions';
import paths from '@rental-manager/pathsManifest';
import { isHighRiskOTP } from '@common/paymentsUtils/moveInCostsUtil';
import OneTimePaymentTypes from '@common/constants/oneTimePaymentTypes';
import { resetNewTermSettings } from '@rental-manager/actions/DashboardActions';
import { logError } from '@zg-rentals/log-error';
import HTTP_STATUS from '@common/constants/HttpConstants';
import { getSignInPage } from '@common/paymentsUtils/api';
import { getPaymentErrorInfo } from '../../../../utils/getPaymentErrorInfo';

const mapStateToProps = (
  state,
  { propertyId, fromDashboard = false, goToExperianFlow, goToConfirmation, setupNewTerm, listing },
) => {
  const { moveInCostsSettings, leaseId } = state.payments.landlord.onboarding[propertyId];
  const { featurePermissions, currentTerm, nextTerm, isHighRiskOtpApproved, selectedLease, leasesForProperty } =
    state.payments.landlord.property;
  const rentTermsKey = nextTerm?.rentTermsKey || currentTerm.rentTermsKey;

  // featurePermissions is only fetched for the current rentTermsKey by the term-length submit,
  // so a direct load or mid-flow refresh reaches this screen before the key exists.
  const permissionsForTerm = featurePermissions?.[rentTermsKey] ?? {};
  const individualLimits = {
    deposit: permissionsForTerm[OneTimePaymentTypes.DEPOSIT]?.amountLimit,
    proratedRent: permissionsForTerm[OneTimePaymentTypes.PRORATED_RENT]?.amountLimit,
    other: permissionsForTerm[OneTimePaymentTypes.OTHER]?.amountLimit,
  };
  const { securityDepositSettings, proratedRentSettings, otherSettings, moveInCostsPaymentLimits, dueDate, editMode } =
    moveInCostsSettings;
  const { securityDepositPaymentAmount, securityDepositNotes, securityDepositStartsOpen } = securityDepositSettings;
  const { proratedRentPaymentAmount, proratedRentNotes, proratedRentStartsOpen } = proratedRentSettings;
  let otherTotalPaymentAmount = 0;

  otherSettings.forEach((setting) => {
    otherTotalPaymentAmount += setting.otherPaymentAmount;
  });
  const totalPaymentAmount = securityDepositPaymentAmount + proratedRentPaymentAmount + otherTotalPaymentAmount;
  const isHighRiskOTPPresent = isHighRiskOTP(moveInCostsSettings);
  const selectedLeaseDetails = setupNewTerm && leaseId ? leasesForProperty[leaseId] : selectedLease;

  const {
    proratedRent: proratedRentFromLease,
    securityDeposit: securityDepositFromLease,
    securityDepositMandates: securityDepositMandatesFromLease,
    otherMic: otherMicFromLease,
  } = selectedLeaseDetails || {};

  const securityDepositFromListing = listing?.floorplans?.[0]?.securityDeposit;
  const securityDepositFromListingInCents = securityDepositFromListing ? securityDepositFromListing * 100 : 0;

  const finalSecurityDeposit = securityDepositFromLease || securityDepositFromListingInCents;

  return {
    securityDepositPaymentAmount,
    securityDepositNotes,
    securityDepositStartsOpen,
    proratedRentPaymentAmount,
    proratedRentNotes,
    proratedRentStartsOpen,
    otherSettings,
    otherTotalPaymentAmount,
    moveInCostsPaymentLimits,
    totalPaymentAmount,
    dueDate,
    editMode,
    fromDashboard,
    featurePermissions,
    rentTerms: nextTerm?.rentTermsKey ? nextTerm : currentTerm,
    isHighRiskOTPPresent,
    individualLimits,
    isHighRiskOtpApproved,
    proratedRentFromLease,
    securityDeposit: finalSecurityDeposit,
    otherMicFromLease,
    securityDepositMandatesFromLease,
    goToExperianFlow,
    goToConfirmation,
    setupNewTerm,
    user: state.user,
    currentProperty: state.payments.landlord.property.listingDetails,
  };
};

const mapDispatchToProps = (dispatch, { baseUrl, propertyId, fromDashboard, setupNewTerm = false }): any => ({
  saveStateToReduxStore: (payload) =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => dispatch(saveMoveInCostsSettings({ propertyId, ...payload })))
      .then(() => ({ success: true }))
      .catch((err) => {
        logError({ error: err });
        dispatch(showModalNotification(DEFAULT_ERROR, { subject: 'Move-in costs' }));
        return { error: true };
      }),
  onSubmit: (editMode, isHighRiskOTPPresent, isHighRiskOtpApproved) =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => {
        const experianFlowRequired = isHighRiskOTPPresent && !isHighRiskOtpApproved;

        if ((editMode || fromDashboard) && experianFlowRequired) {
          return dispatch(
            push({
              pathname: baseUrl + paths.onboarding.knowledgeBasedAuthenticationPage,
              state: editMode ? { fromWhere: 'onboardingReview' } : undefined,
            }),
          );
        }
        if (fromDashboard) {
          return dispatch(push(baseUrl + paths.dashboard.confirmMoveInCostsPage));
        }
        if (editMode) {
          return dispatch(push(baseUrl + paths.onboarding.confirmationPage));
        }
        return dispatch(push(baseUrl + paths.onboarding.inviteTenantPage));
      })
      .then(() => dispatch(getIdVerificationInfo({ propertyId })))
      .then(() => editMode && !fromDashboard && dispatch(showModalNotification(MOVE_IN_COSTS_SETTINGS_UPDATED)))
      .catch((err) => {
        logError({ error: err });
        dispatch(showModalNotification(DEFAULT_ERROR, { subject: 'Move-in costs' }));
        return { error: true };
      }),
  onSubmitFromSetupNewTerm: (editMode, isHighRiskOTPPresent, isHighRiskOtpApproved) =>
    Promise.resolve()
      .then(() => dispatch(getIdVerificationInfo({ propertyId })))
      .then(() => editMode && dispatch(showModalNotification(MOVE_IN_COSTS_SETTINGS_UPDATED)))
      .then(() => {
        const experianFlowRequired = isHighRiskOTPPresent && !isHighRiskOtpApproved;
        if (setupNewTerm) {
          return {
            success: true,
            experianFlowRequired,
          };
        }
      })
      .catch((err) => {
        logError({ error: err });
        dispatch(showModalNotification(DEFAULT_ERROR, { subject: 'Move-in costs' }));
        return { error: true };
      }),
  onBack: () =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => dispatch(goBack())),
  onClose: () =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => setupNewTerm && dispatch(resetNewTermSettings()))
      .then(() => {
        if (fromDashboard || setupNewTerm) {
          return Promise.resolve()
            .then(() => dispatch(push(baseUrl)))
            .then(() =>
              dispatch(getLandlordStatus({ propertyId })).catch((err) => {
                const { httpStatus } = getPaymentErrorInfo(err);
                if (httpStatus === HTTP_STATUS.SC_UNAUTHORIZED) {
                  window.location = getSignInPage();
                }
              }),
            );
        }
        return dispatch(push(baseUrl + paths.onboarding.exitPaymentSetup));
      }),
  onCancel: () =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => dispatch(push(baseUrl))),
  showModalNotification: (notificationType) => dispatch(showModalNotification(notificationType)),
});
const mergeProps = (stateProps, dispatchProps) => ({
  ...stateProps,
  ...dispatchProps,
  onSubmit: (editMode) => {
    if (stateProps.setupNewTerm) {
      return dispatchProps.onSubmitFromSetupNewTerm(
        editMode,
        stateProps.isHighRiskOTPPresent,
        stateProps.isHighRiskOtpApproved,
      );
    } else {
      return dispatchProps.onSubmit(editMode, stateProps.isHighRiskOTPPresent, stateProps.isHighRiskOtpApproved);
    }
  },
});
export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(MoveInCostsPage);
